import { useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import {
    api, ApiError, newIdempotencyKey,
    type CreateTaskBody, type GameId, type Role, type SpecialType,
} from '../lib/api';

type Props = {
    onClose:   () => void;
    onPublish: (data: { id: number; totalCost: number; status: 'pending' | 'approved' }) => void;
    role:      Role;
    commissionPct:    number;
    holdPricePerHour: number;
};

type TaskType = 'fast' | 'regular' | 'special';

const SPECIAL_LABELS: Record<SpecialType, string> = {
    play_game:      'Сыграть в определённую игру',
    play_each_game: 'Сыграть в каждую игру',
    deposit_amount: 'Пополнить на определённую сумму',
    daily_bonus:    'Ежедневный бонус',
    deposit_ladder: 'Сделать депозит сегодня (+% к следующему)',
};

const GAMES: Array<{ id: GameId; label: string }> = [
    { id: 'boxes', label: 'BSP BOXES' }, { id: 'fast', label: 'FAST GIVEAWAY' },
    { id: 'g667', label: '667' }, { id: 'battle', label: 'BOG BATTLE' }, { id: 'piggy', label: 'PIGGY BANK' },
];

/** The canvas's own examples, one tap to fill the form. */
const PRESETS: Array<{ label: string; title: string; type: SpecialType; reward: string; game?: GameId; count?: string }> = [
    { label: 'Депозит сегодня', title: 'Сделай депозит сегодня', type: 'deposit_ladder', reward: '0' },
    { label: 'Каждая игра · 10', title: 'Сыграй в каждую игру в боте', type: 'play_each_game', reward: '10' },
    { label: '25 батлов · 25', title: 'Сразись 25 раз в батлах', type: 'play_game', reward: '25', game: 'battle', count: '25' },
];

const ERRORS: Record<string, string> = {
    insufficient_balance:   'Недостаточно BSP на балансе.',
    bot_not_admin:          'Наш бот не администратор этого канала. Добавьте его в администраторы со всеми правами и повторите.',
    bot_check_failed:       'Не удалось проверить канал. Проверьте ссылку и что бот добавлен в канал.',
    channel_url_unsupported:'Нужна публичная ссылка вида t.me/имя_канала — приватные инвайт-ссылки бот проверить не может.',
    control_word_required:  'Укажите проверочное слово — одно слово без пробелов.',
    root_only:              'Особые задания создаёт только root.',
    reward_required:        'Укажите награду.',
};

export function CreateTaskModal({ onClose, onPublish, role, commissionPct, holdPricePerHour }: Props) {
    const isRoot = role === 'root';
    const isStaff = role === 'root' || role === 'admin';
    const [type, setType] = useState<TaskType>('regular');

    const [title, setTitle]   = useState('');
    const [desc, setDesc]     = useState('');
    const [reward, setReward] = useState('');
    const [slots, setSlots]   = useState('');
    const [premium, setPremium] = useState(false);
    // fast
    const [channel, setChannel] = useState('');
    const [hold, setHold]       = useState('0');
    // regular
    const [instructions, setInstructions] = useState('');
    const [checkType, setCheckType] = useState<'screenshot' | 'word'>('screenshot');
    const [word, setWord] = useState('');
    const [link, setLink] = useState('');
    // special
    const [special, setSpecial] = useState<SpecialType>('play_game');
    const [game, setGame]       = useState<GameId>('battle');
    const [count, setCount]     = useState('1');
    const [amount, setAmount]   = useState('');
    const [stepPct, setStepPct] = useState('5');
    const [maxPct, setMaxPct]   = useState('15');
    const [limit, setLimit]     = useState('100000');

    const [busy, setBusy] = useState(false);
    const [err, setErr]   = useState<string | null>(null);

    const rewardNum = Number.parseInt(reward, 10);
    const slotsNum  = Number.parseInt(slots, 10);
    const holdNum   = Number.parseInt(hold, 10) || 0;
    const ladder    = type === 'special' && special === 'deposit_ladder';

    const total = useMemo(() => {
        if (type === 'special' || !Number.isFinite(rewardNum) || !Number.isFinite(slotsNum)) return 0;
        const base = rewardNum * slotsNum;
        const fee = Math.ceil((base * commissionPct) / 100);
        const holdFee = type === 'fast' ? Math.ceil(holdPricePerHour * holdNum * slotsNum) : 0;
        const premiumFee = premium ? 3 * slotsNum : 0;
        const wordFee = type === 'regular' && checkType === 'word' ? Math.ceil(0.5 * slotsNum) : 0;
        return Math.ceil(base + fee + holdFee + premiumFee + wordFee);
    }, [type, rewardNum, slotsNum, commissionPct, holdPricePerHour, holdNum, premium, checkType]);

    const valid =
        title.trim().length >= 2 &&
        (type === 'special'
            ? (ladder || (Number.isFinite(rewardNum) && rewardNum >= 1)) &&
              (special !== 'deposit_amount' || Number.parseInt(amount, 10) >= 1) &&
              (special !== 'play_game' || Number.parseInt(count, 10) >= 1)
            : Number.isFinite(rewardNum) && rewardNum >= 1 && Number.isFinite(slotsNum) && slotsNum >= 1 &&
              (type === 'fast'
                  ? channel.trim().length > 0
                  : instructions.trim().length > 0 && (checkType === 'screenshot' || /^\S+$/.test(word.trim()))));

    const applyPreset = (p: (typeof PRESETS)[number]) => {
        setType('special'); setSpecial(p.type); setTitle(p.title); setReward(p.reward);
        if (p.game) setGame(p.game);
        if (p.count) setCount(p.count);
    };

    const submit = async () => {
        if (!valid || busy) return;
        setBusy(true); setErr(null);
        let body: CreateTaskBody;
        const common = { title: title.trim(), description: desc.trim() || undefined };
        if (type === 'fast') {
            body = { type, ...common, url: normaliseChannel(channel), reward: rewardNum, slots: slotsNum, holdHours: holdNum, isPremiumOnly: premium };
        } else if (type === 'regular') {
            body = {
                type, ...common, reward: rewardNum, slots: slotsNum, instructions: instructions.trim(),
                checkType, controlWord: checkType === 'word' ? word.trim().toLowerCase() : undefined,
                url: link.trim() || undefined, isPremiumOnly: premium,
            };
        } else {
            const params: Record<string, unknown> =
                special === 'play_game' ? { game, count: Number.parseInt(count, 10) } :
                special === 'deposit_amount' ? { amount: Number.parseInt(amount, 10) } :
                special === 'deposit_ladder' ? { stepPct: Number(stepPct), maxPct: Number(maxPct) } : {};
            body = {
                type, ...common, specialType: special, params,
                reward: ladder ? 0 : rewardNum, slots: Number.parseInt(limit, 10) || 100000,
            };
        }
        try {
            const r = await api.createTask(newIdempotencyKey(), body);
            onPublish(r);
        } catch (e) {
            setErr(e instanceof ApiError ? (ERRORS[e.code] ?? `Ошибка: ${e.code}`) : String(e));
        } finally { setBusy(false); }
    };

    return (
        <Modal title="Создать задание" onClose={onClose}>
            <div className="seg" role="tablist" aria-label="Тип задания">
                <button type="button" className={type === 'fast' ? 'active' : ''} onClick={() => setType('fast')}>Быстрое</button>
                <button type="button" className={type === 'regular' ? 'active' : ''} onClick={() => setType('regular')}>Обычное</button>
                {isRoot && (
                    <button type="button" className={type === 'special' ? 'active' : ''} onClick={() => setType('special')}>Особое</button>
                )}
            </div>
            <p className="popup-text text-muted f-12" style={{ marginTop: 6 }}>
                {type === 'fast' && 'Подписка на канал — бот сам проверяет подписку исполнителя.'}
                {type === 'regular' && 'Любое действие — проверка по скриншоту или проверочному слову.'}
                {type === 'special' && 'Выполняется действиями в боте: игры, пополнения, бонусы. Оплачивает касса.'}
            </p>

            {type === 'special' && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {PRESETS.map((p) => (
                        <button key={p.label} type="button" className="task-filter" onClick={() => applyPreset(p)}>{p.label}</button>
                    ))}
                </div>
            )}

            <div className="input-group">
                <label>Название</label>
                <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)}
                    placeholder={type === 'fast' ? 'Подпишись на @BogStarsNews' : 'Сделай репост поста'} />
            </div>
            <div className="input-group">
                <label>Описание</label>
                <textarea rows={2} maxLength={700} value={desc} onChange={(e) => setDesc(e.target.value)}
                    placeholder="Коротко: что нужно сделать" />
            </div>

            {type === 'fast' && (
                <>
                    <div className="input-group">
                        <label>Ссылка на канал</label>
                        <input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="https://t.me/имя_канала" />
                        <span className="hint">Бот должен быть администратором канала со всеми правами — иначе он не сможет проверить подписку.</span>
                    </div>
                    <div className="input-group">
                        <label>Холд, часов (0 — без холда)</label>
                        <input type="number" inputMode="numeric" min={0} max={720} value={hold} onChange={(e) => setHold(e.target.value)} />
                        <span className="hint">Пока идёт холд, отписка отменяет выполнение. +{holdPricePerHour} BSP за час на исполнение.</span>
                    </div>
                </>
            )}

            {type === 'regular' && (
                <>
                    <div className="input-group">
                        <label>Инструкции к выполнению (видны после начала)</label>
                        <textarea rows={3} maxLength={3000} value={instructions} onChange={(e) => setInstructions(e.target.value)}
                            placeholder="Небольшие инструкции, которые вы должны предоставить исполнителю" />
                    </div>
                    <div className="input-group">
                        <label>Тип проверки</label>
                        <div className="seg">
                            <button type="button" className={checkType === 'screenshot' ? 'active' : ''} onClick={() => setCheckType('screenshot')}>Скриншот</button>
                            <button type="button" className={checkType === 'word' ? 'active' : ''} onClick={() => setCheckType('word')}>Проверочное слово</button>
                        </div>
                    </div>
                    {checkType === 'word' && (
                        <div className="input-group">
                            <label>Проверочное слово (без учёта регистра)</label>
                            <input value={word} maxLength={50} autoComplete="off"
                                onChange={(e) => setWord(e.target.value.replace(/\s/g, ''))} placeholder="богстарс" />
                            <span className="hint">Правильное слово засчитывается сразу. При неверном исполнитель может запросить ручную проверку — сначала у вас.</span>
                        </div>
                    )}
                    <div className="input-group">
                        <label>Ссылка для перехода (необязательно)</label>
                        <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
                    </div>
                </>
            )}

            {type === 'special' && (
                <>
                    <div className="input-group">
                        <label>Механика</label>
                        <select value={special} onChange={(e) => setSpecial(e.target.value as SpecialType)}>
                            {(Object.keys(SPECIAL_LABELS) as SpecialType[]).map((k) => (
                                <option key={k} value={k}>{SPECIAL_LABELS[k]}</option>
                            ))}
                        </select>
                    </div>
                    {special === 'play_game' && (
                        <div style={{ display: 'flex', gap: 8 }}>
                            <div className="input-group" style={{ flex: 2 }}>
                                <label>Игра</label>
                                <select value={game} onChange={(e) => setGame(e.target.value as GameId)}>
                                    {GAMES.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                                </select>
                            </div>
                            <div className="input-group" style={{ flex: 1 }}>
                                <label>Сколько раз</label>
                                <input type="number" inputMode="numeric" min={1} value={count} onChange={(e) => setCount(e.target.value)} />
                            </div>
                        </div>
                    )}
                    {special === 'deposit_amount' && (
                        <div className="input-group">
                            <label>Сумма пополнений, BSP</label>
                            <input type="number" inputMode="numeric" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
                        </div>
                    )}
                    {ladder && (
                        <div style={{ display: 'flex', gap: 8 }}>
                            <div className="input-group" style={{ flex: 1 }}>
                                <label>Шаг, %</label>
                                <input type="number" inputMode="decimal" value={stepPct} onChange={(e) => setStepPct(e.target.value)} />
                            </div>
                            <div className="input-group" style={{ flex: 1 }}>
                                <label>Максимум, %</label>
                                <input type="number" inputMode="decimal" value={maxPct} onChange={(e) => setMaxPct(e.target.value)} />
                            </div>
                        </div>
                    )}
                </>
            )}

            {!ladder && (
                <div style={{ display: 'flex', gap: 8 }}>
                    <div className="input-group" style={{ flex: 1 }}>
                        <label>{type === 'special' ? 'Награда, BSP' : 'Стоимость 1 исполнения'}</label>
                        <input type="number" inputMode="numeric" min={1} step={1} value={reward} onChange={(e) => setReward(e.target.value)} />
                    </div>
                    <div className="input-group" style={{ flex: 1 }}>
                        <label>{type === 'special' ? 'Лимит выполнений' : 'Количество выполнений'}</label>
                        {type === 'special'
                            ? <input type="number" inputMode="numeric" min={1} value={limit} onChange={(e) => setLimit(e.target.value)} />
                            : <input type="number" inputMode="numeric" min={1} value={slots} onChange={(e) => setSlots(e.target.value)} />}
                    </div>
                </div>
            )}

            {type !== 'special' && (
                <label className="checkbox-row" style={{ justifyContent: 'flex-start', gap: 10, marginTop: 4 }}>
                    <input type="checkbox" checked={premium} onChange={(e) => setPremium(e.target.checked)} />
                    <span className="f-12" style={{ color: '#ccc' }}>Только для Telegram Premium (+5 BSP/исполнение, исполнитель получает +2)</span>
                </label>
            )}

            {type !== 'special' && (
                <div className="calc-box">
                    <span className="text-muted f-12">Итого к списанию (с комиссией {commissionPct}%):</span>
                    <div className="text-accent fw-900" style={{ fontSize: 18, marginTop: 4 }} data-testid="task-total">
                        {total.toLocaleString('ru-RU')} BSP
                    </div>
                    {!isStaff && (
                        <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                            После создания задание уйдёт на модерацию. При отказе средства могут вернуть — или нет, если задание нарушает правила.
                        </span>
                    )}
                </div>
            )}

            {err && <p className="popup-text" style={{ color: 'var(--red)' }}>{err}</p>}

            <button type="button" className="action-btn btn-gold" disabled={!valid || busy} onClick={submit}>
                {busy ? '…' : isStaff || type === 'special' ? 'Опубликовать' : 'Отправить на модерацию'}
            </button>
        </Modal>
    );
}

/** Accept "@name", "name" or a full link for the channel. */
function normaliseChannel(v: string): string {
    const t = v.trim();
    if (/^https?:\/\//i.test(t)) return t;
    if (/^t\.me\//i.test(t)) return `https://${t}`;
    return `https://t.me/${t.replace(/^@/, '')}`;
}
